import { Search, Target, BarChart3, Layers, Cpu, RefreshCw } from "lucide-react";
import AdsPlatformPage from "../components/site/AdsPlatformPage";
import { usePageSEO, breadcrumb, serviceSchema, faqSchema } from "../hooks/usePageSEO";

const config = {
  meta: {
    eyebrow: "Google Ads",
  },
  hero: {
    title: "Captura intención de compra y",
    highlight: "convierte tráfico caliente",
  },
  intro: {
    eyebrow: "Performance con intención",
    title: "Campañas conectadas a tu sistema, no campañas sueltas.",
    body:
      "Google es donde la gente levanta la mano. Mi trabajo es asegurarme de que cada clic entra a un sistema diseñado para convertir, no a una landing huérfana sin seguimiento.",
  },
  services: [
    { icon: Search, title: "Search · Generación de leads", desc: "Campañas de intención con keywords B2B y B2C, copy estratégico y extensiones afinadas." },
    { icon: Target, title: "Performance Max", desc: "PMax bien gobernada: feeds, audiencias, exclusiones y señales de conversión reales." },
    { icon: Layers, title: "YouTube Ads", desc: "Activos de video para top y mid funnel con tracking quirúrgico." },
    { icon: BarChart3, title: "Tracking & atribución", desc: "GA4, GTM, server-side, conversiones offline desde tu CRM y micro-eventos." },
    { icon: Cpu, title: "Automatización conectada", desc: "Cada lead entra a flujos de IA, Respond.io y CRM sin intervención manual." },
    { icon: RefreshCw, title: "Optimización continua", desc: "Iteración semanal sobre keywords, copy, audiencias y bidding strategies." },
  ],
  approach: [
    "Pixel y tracking server-side configurado antes de gastar el primer peso.",
    "Conversiones offline desde tu CRM como fuente de verdad para el algoritmo.",
    "Exclusiones, negativas y filtros de calidad para no quemar presupuesto.",
    "Reportes ejecutivos enfocados en CAC, LTV y ROAS — no en CTR.",
    "Cada lead conecta automáticamente con WhatsApp, IG o el canal donde respondes.",
    "Iteración semanal con creativas, copies y audiencias frescas.",
  ],
  whyItems: [
    { title: "Operador, no gestor", desc: "Yo configuro, mido, optimizo y hablo contigo. Sin account managers intermedios." },
    { title: "Stack completo conectado", desc: "Las campañas no viven solas: alimentan IA, automatización y un CRM real." },
    { title: "Decisiones por dato, no estética", desc: "Reportes claros con accionables — no dashboards bonitos sin conclusiones." },
  ],
  faqs: [
    { q: "¿Inversión mínima recomendada?", a: "Para que el algoritmo aprenda y los datos sean significativos, recomiendo desde $1,500 USD/mes. Por debajo de eso, mejor enfocar otra plataforma." },
    { q: "¿En cuánto tiempo veo resultados?", a: "La primera ola estable de leads aparece entre la semana 2 y 4. La optimización fina llega después del segundo mes con datos suficientes." },
    { q: "¿Trabajas e-commerce o solo B2B?", a: "Ambos. Cambia la estrategia, las señales de conversión y el feed, pero el método de implementación es el mismo." },
  ],
  related: [
    { to: "/meta-ads", label: "Meta Ads", desc: "Combina con Facebook + Instagram para captura de demanda y remarketing." },
    { to: "/automatizacion-ia", label: "Automatización con IA", desc: "Que cada lead entre a un flujo automatizado, no a un excel." },
    { to: "/funnels", label: "Funnels", desc: "Sistema completo conectado a Google Ads." },
  ],
  resultStat: {
    value: "+20",
    label: "Implementaciones de Ads + automatización trabajadas",
    footnote: "Datos auditables disponibles en consultoría.",
  },
};

export default function GoogleAdsPage() {
  usePageSEO({
    title: "Google Ads para Empresas | Daos Ads",
    description:
      "Implementación y operación experta de Google Ads: Search, Performance Max, YouTube y tracking server-side conectado a IA, CRM y automatización. Leads calificados con ROAS real.",
    path: "/google-ads",
    jsonLd: [
      breadcrumb([
        { name: "Inicio", path: "/" },
        { name: "Google Ads", path: "/google-ads" },
      ]),
      serviceSchema({
        name: "Google Ads para empresas",
        description:
          "Gestión experta de campañas Google Ads (Search, PMax, YouTube) con tracking server-side, Conversion API y automatización de seguimiento.",
        path: "/google-ads",
      }),
      faqSchema(config.faqs),
    ],
  });
  return <AdsPlatformPage {...config} />;
}
